import {FlexColumn, Modal, Pill, PillVariant, SubHeading, Table} from '@forecast-it/design-system';
import moment from 'moment';
import React from 'react';
import {useIntl} from 'react-intl';
import {currencies} from '../../../constants';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import type {ModalProps} from '../../shared/components/modals/types';
import Util from '../../shared/util/util';
import type {ExchangeRate, RateStatus} from './types';
import {getRateStatus} from './ExchangeRateUtil';

export type CurrencyHistoryModalPayload = {
	currencyCode: string;
	exchangeRates: ExchangeRate[];
};

export const showCurrencyHistoryModal = (props: CurrencyHistoryModalPayload): void => {
	showModal({
		type: MODAL_TYPE.CURRENCY_HISTORY,
		...props,
	});
};

type CurrencyHistoryModalProps = ModalProps<CurrencyHistoryModalPayload>;

const STATUS_VARIANTS: Record<RateStatus, PillVariant> = {
	current: PillVariant.SUCCESS,
	past: PillVariant.NEUTRAL,
	upcoming: PillVariant.INFORMATION,
};

export const CurrencyHistoryModal = ({currencyCode, exchangeRates, closeModal}: CurrencyHistoryModalProps) => {
	const {formatMessage} = useIntl();

	const currencyEntry = currencies.find(c => c.code === currencyCode);
	const currencyLabel = currencyEntry
		? `${currencyEntry.code} (${currencyEntry.symbol}) - ${currencyEntry.name}`
		: currencyCode;

	const today = moment();

	const sorted = [...exchangeRates].sort((a, b) => {
		if (a.startDate === null) return -1;
		if (b.startDate === null) return 1;
		return a.startDate.localeCompare(b.startDate);
	});

	const historyRows = sorted.map(rate => {
		const startLabel =
			rate.startDate !== null
				? moment.utc(rate.startDate).format('DD MMM YYYY')
				: formatMessage({id: 'settings_finance.currencies.history_modal.initial'});

		const endLabel =
			rate.endDate !== null
				? moment.utc(rate.endDate).format('DD MMM YYYY')
				: formatMessage({id: 'settings_finance.currencies.history_modal.onwards'});

		const status = getRateStatus(rate, today);

		const thisRate = rate.rate ?? 0;

		return {
			id: rate.id,
			startLabel,
			endLabel,
			thisRate,
			rateCurrency: rate.currency,
			createdAtFormatted: Util.formatUtcDateTimeToLocalTimezone(rate.createdAt),
			status,
		};
	});

	return (
		<Modal size={Modal.SIZE.LARGE} onCloseModal={closeModal}>
			<Modal.Title>{formatMessage({id: 'settings_finance.currencies.history_modal.title'})}</Modal.Title>
			<Modal.Content>
				<FlexColumn gap="m">
					<SubHeading size={1}>{currencyLabel}</SubHeading>
					<Table width="100%" data={historyRows}>
						<Table.TextColumn
							accessorKey="effectiveFrom"
							header={formatMessage({id: 'settings_finance.currencies.history_modal.effective_from'})}
							icon={(row: {startLabel: string; endLabel: string}) => (
								<>
									<SubHeading size={2}>{`${row.startLabel} `}</SubHeading>
									{`- ${row.endLabel}`}
								</>
							)}
						/>
						<Table.TextColumn
							accessorKey="rate"
							header={formatMessage({id: 'common.rate'})}
							icon={(row: {thisRate: number; rateCurrency: string}) => (
								<SubHeading size={2}>{`${row.thisRate} ${row.rateCurrency}`}</SubHeading>
							)}
						/>
						<Table.TextColumn
							accessorKey="createdAtFormatted"
							header={formatMessage({id: 'global_filter_field.created_at'})}
						/>
						<Table.TextColumn
							accessorKey="statusIcon"
							header={formatMessage({id: 'settings_finance.currencies.history_modal.status'})}
							icon={(row: {status: RateStatus}) => (
								<Pill variant={STATUS_VARIANTS[row.status]}>
									{formatMessage({id: `settings_finance.currencies.history_modal.status_${row.status}`})}
								</Pill>
							)}
						/>
					</Table>
				</FlexColumn>
			</Modal.Content>
		</Modal>
	);
};
